import { useState, useMemo, useEffect } from "react";
import { useQuery } from "@tanstack/react-query";
import { api } from "@/lib/api";
import { usePermissions } from "@/hooks/use-permissions";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogClose,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  SearchIcon,
  PlusIcon,
  PencilIcon,
  Trash2Icon,
  RotateCcwIcon as RestoreIcon,
} from "lucide-react";
import { Pagination } from "@/components/ui/pagination";
import { useCargoProductMutations } from "./useCargoProductMutations";
import { CargoProductFormSheet } from "./CargoProductFormSheet";

const PAGE_SIZE = 25;

interface CargoProductRow {
  id: string;
  name: string;
  code: string;
  sgtCodes: string[] | null;
  cargoTypeId: string | null;
  cargoTypeName: string | null;
  deletedAt: string | null;
  createdAt: string;
  updatedAt: string;
}

type FilterStatus = "active" | "deleted" | "all";

interface Props {
  defaultSearch?: string;
  hideHeader?: boolean;
}

export function CargoProductsTable({ defaultSearch = "", hideHeader = false }: Props) {
  const { can } = usePermissions();

  const [search, setSearch]             = useState(defaultSearch);
  const [filterStatus, setFilterStatus] = useState<FilterStatus>("active");
  const [page, setPage]                 = useState(1);
  const [sheetOpen, setSheetOpen]       = useState(false);
  const [deleteOpen, setDeleteOpen]     = useState(false);
  const [editing, setEditing]           = useState<CargoProductRow | null>(null);
  const [deleting, setDeleting]         = useState<CargoProductRow | null>(null);
  const [formError, setFormError]       = useState<string | null>(null);
  const [restoringId, setRestoringId]   = useState<string | null>(null);
  const [filterCargoType, setFilterCargoType] = useState("all");
  const [filterSgt, setFilterSgt]       = useState<"all" | "with" | "without">("all");

  useEffect(() => {
    if (defaultSearch) {
      setSearch(defaultSearch);
      setPage(1);
    }
  }, [defaultSearch]);

  const { data: cargoTypesList = [] } = useQuery({
    queryKey: ["cargos", "types", "list"],
    queryFn: async () => {
      const res = await api.cargos.types.$get({ query: { deleted: "false" } });
      if (!res.ok) return [];
      return res.json() as Promise<{ id: string; code: string; name: string }[]>;
    },
    staleTime: 60000,
  });

  const { data: cargoProducts = [], isLoading } = useQuery({
    queryKey: ["cargos", "products", { status: filterStatus }],
    queryFn: async () => {
      const query: any = {};
      if (filterStatus === "deleted") query.deleted = "true";
      if (filterStatus === "all")     query.deleted = "all";

      const res = await api.cargos.products.$get({ query });
      return res.json() as Promise<CargoProductRow[]>;
    },
  });

  const filtered = useMemo(() => {
    const term = search.toLowerCase().trim();
    return cargoProducts.filter((i) => {
      const matchesSearch =
        !term ||
        i.name.toLowerCase().includes(term) ||
        i.code.toLowerCase().includes(term) ||
        (i.sgtCodes ?? []).some((s) => s.toLowerCase().includes(term));
      const matchesType = filterCargoType === "all" || i.cargoTypeId === filterCargoType;

      const hasSgt = (i.sgtCodes?.length ?? 0) > 0;
      const matchesSgt = filterSgt === "all" || (filterSgt === "with" && hasSgt) || (filterSgt === "without" && !hasSgt);

      return matchesSearch && matchesType && matchesSgt;
    });
  }, [cargoProducts, search, filterCargoType, filterSgt]);

  const totalPages = Math.max(1, Math.ceil(filtered.length / PAGE_SIZE));
  const paged      = filtered.slice((page - 1) * PAGE_SIZE, page * PAGE_SIZE);

  const { createMut, updateMut, deleteMut, restoreMut, isSaving } = useCargoProductMutations({
    onSuccess: () => {
      setSheetOpen(false);
      setDeleteOpen(false);
      setEditing(null);
      setDeleting(null);
      setFormError(null);
    },
    onError: (err) => setFormError(err.message),
    onRestoreStart: (id) => setRestoringId(id),
    onRestoreEnd: () => setRestoringId(null),
  });

  function openCreate() {
    setEditing(null);
    setFormError(null);
    setSheetOpen(true);
  }

  function openEdit(item: CargoProductRow) {
    setEditing(item);
    setFormError(null);
    setSheetOpen(true);
  }

  function handleSave(id: string | null, data: any) {
    if (id) {
      updateMut.mutate({ id, data });
    } else {
      createMut.mutate(data);
    }
  }

  return (
    <div className="space-y-4 w-full">
      <div className="flex flex-wrap items-center justify-between gap-4">
        <div className="flex flex-wrap items-center gap-2">
          <div className="relative w-64">
            <SearchIcon className="absolute left-2.5 top-2.5 h-4 w-4 text-muted-foreground" />
            <Input
              placeholder="Buscar por nombre o código..."
              className="pl-8"
              value={search}
              onChange={(e) => { setSearch(e.target.value); setPage(1); }}
            />
          </div>

          <Select value={filterCargoType} onValueChange={(v: string | null) => { setFilterCargoType(v ?? ""); setPage(1); }}>
            <SelectTrigger className="w-[180px]">
              <SelectValue placeholder="Tipo">
                {filterCargoType === "all" ? "Todos los tipos" : (cargoTypesList.find(t => t.id === filterCargoType)?.name ?? filterCargoType)}
              </SelectValue>
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos los tipos</SelectItem>
              {cargoTypesList.map((t) => (
                <SelectItem key={t.id} value={t.id}>{t.name}</SelectItem>
              ))}
            </SelectContent>
          </Select>

          <Select value={filterSgt} onValueChange={(v: any) => { setFilterSgt(v); setPage(1); }}>
            <SelectTrigger className="w-[140px]">
              <SelectValue>
                {filterSgt === "all" ? "Todos (SGT)" : filterSgt === "with" ? "Con SGT" : "Sin SGT"}
              </SelectValue>
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="all">Todos (SGT)</SelectItem>
              <SelectItem value="with">Con SGT</SelectItem>
              <SelectItem value="without">Sin SGT</SelectItem>
            </SelectContent>
          </Select>

          <Select value={filterStatus} onValueChange={(v: any) => { setFilterStatus(v); setPage(1); }}>
            <SelectTrigger className="w-[110px]">
              <SelectValue />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="active">activos</SelectItem>
              <SelectItem value="deleted">borrados</SelectItem>
              <SelectItem value="all">todos</SelectItem>
            </SelectContent>
          </Select>
        </div>

        {can("write", "cargo") && (
          <Button onClick={openCreate} className="gap-2">
            <PlusIcon className="size-4" /> Nuevo producto
          </Button>
        )}
      </div>

      <div className="rounded-md border bg-card overflow-hidden">
        <Table>
          <TableHeader>
            <TableRow>
              <TableHead className="w-[120px]">Código</TableHead>
              <TableHead>Nombre</TableHead>
              <TableHead>Tipo</TableHead>
              <TableHead>SGT Codes</TableHead>
              {can("write", "cargo") && <TableHead className="w-[100px] text-right pr-4">Acciones</TableHead>}
            </TableRow>
          </TableHeader>
          <TableBody>
            {isLoading ? (
              <TableRow>
                <TableCell colSpan={can("write", "cargo") ? 5 : 4} className="h-24 text-center text-muted-foreground">
                  Cargando productos...
                </TableCell>
              </TableRow>
            ) : paged.length === 0 ? (
              <TableRow>
                <TableCell colSpan={can("write", "cargo") ? 5 : 4} className="h-24 text-center text-muted-foreground">
                  No se encontraron productos.
                </TableCell>
              </TableRow>
            ) : (
              paged.map((item) => (
                <TableRow key={item.id} className={item.deletedAt ? "opacity-50 italic" : ""}>
                  <TableCell className="font-mono font-medium text-xs">{item.code}</TableCell>
                  <TableCell className="font-medium">{item.name}</TableCell>
                  <TableCell>
                    {item.cargoTypeName ? (
                      <span className="text-xs text-muted-foreground uppercase">{item.cargoTypeName}</span>
                    ) : <span className="text-muted-foreground/40">—</span>}
                  </TableCell>
                  <TableCell>
                    {item.sgtCodes && item.sgtCodes.length > 0 ? (
                      <div className="flex flex-wrap gap-1 max-w-[300px]">
                        {item.sgtCodes.map((c) => (
                          <span key={c} className="px-1.5 py-0.5 rounded-sm bg-muted font-mono text-[10px] text-muted-foreground">
                            {c}
                          </span>
                        ))}
                      </div>
                    ) : <span className="text-muted-foreground/40 text-xs">—</span>}
                  </TableCell>
                  {can("write", "cargo") && (
                    <TableCell className="text-right pr-4">
                      <div className="flex justify-end gap-1">
                        {item.deletedAt ? (
                          <Button
                            variant="ghost"
                            size="icon-sm"
                            onClick={() => restoreMut.mutate(item.id)}
                            disabled={restoringId === item.id}
                            className="text-emerald-600 hover:text-emerald-700 hover:bg-emerald-50"
                            title="Restaurar"
                          >
                            <RestoreIcon className="size-3.5" />
                          </Button>
                        ) : (
                          <>
                            <Button variant="ghost" size="icon-sm" onClick={() => openEdit(item)} title="Editar">
                              <PencilIcon className="size-3.5" />
                            </Button>
                            <Button
                              variant="ghost"
                              size="icon-sm"
                              onClick={() => { setDeleting(item); setDeleteOpen(true); }}
                              className="text-destructive hover:text-destructive hover:bg-destructive/10"
                              title="Eliminar"
                            >
                              <Trash2Icon className="size-3.5" />
                            </Button>
                          </>
                        )}
                      </div>
                    </TableCell>
                  )}
                </TableRow>
              ))
            )}
          </TableBody>
        </Table>
        <Pagination
          currentPage={page}
          totalPages={totalPages}
          totalItems={filtered.length}
          pageSize={PAGE_SIZE}
          onPageChange={setPage}
        />
      </div>

      <CargoProductFormSheet
        open={sheetOpen}
        onClose={() => setSheetOpen(false)}
        editing={editing}
        cargoTypes={cargoTypesList}
        onSave={handleSave}
        isSaving={isSaving}
        error={formError}
      />

      <Dialog open={deleteOpen} onOpenChange={setDeleteOpen}>
        <DialogContent showCloseButton={false}>
          <DialogHeader>
            <DialogTitle>Eliminar producto</DialogTitle>
            <DialogDescription>
              ¿Eliminás el producto <strong>{deleting?.code}</strong> — {deleting?.name}? Esta acción es reversible desde la opción de restaurar.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <DialogClose>
              <Button variant="outline" onClick={() => setDeleting(null)}>Cancelar</Button>
            </DialogClose>
            <Button
              variant="destructive"
              disabled={deleteMut.isPending}
              onClick={() => deleting && deleteMut.mutate(deleting.id)}
            >
              {deleteMut.isPending ? "Eliminando..." : "Eliminar"}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
